/// <amd-module name="Repos/Views/PullRequestListingPageActions/Components/CustomizeQueriesPanel" />
import * as React from "react";
import { IPullRequestQuery } from "Repos/PullRequestList/Contracts";
import * as Resources from "Repos/Views/PullRequestListingPageActions/Resources";
import { TeamProjectReference } from "Tfs/Platform/Contracts/Core";
import { IReadonlyObservableArray, IReadonlyObservableValue } from "VSS/Core/Observable";
import { Button } from "VSSUI/Button";
import { Icon, IconSize } from "VSSUI/Icon";
import { IListItemDetails, ListItem, ScrollableList } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Pill, PillSize } from "VSSUI/Pill";
import { Spinner } from "VSSUI/Spinner";
import { Tooltip } from "VSSUI/TooltipEx";

export interface IForkDetails {
    repositoryName: string;
    project: TeamProjectReference;
    includeAllBranches: boolean;
}

export interface ICustomizeQueriesPanelProps {
    errorMessage: IReadonlyObservableValue<string>;
    queries: IReadonlyObservableArray<IPullRequestQuery>;
    onDeleteQuery: (query: IPullRequestQuery) => void;
    onDismiss: () => void;
    onEditQuery: (query: IPullRequestQuery) => void;
    onMoveQuery: (query: IPullRequestQuery, up: boolean) => void;
    onNewQuery: () => void;
    onResetQueries: () => void;
    showOverlay: IReadonlyObservableValue<boolean>;
}

export const CustomizeQueriesPanel = (props: ICustomizeQueriesPanelProps) => {
    const { errorMessage, onDeleteQuery, onDismiss, onEditQuery, onMoveQuery, onNewQuery, onResetQueries, queries, showOverlay } = props;
    return (
        <Observer showOverlay={showOverlay}>
            {({ showOverlay }: { showOverlay: boolean }) => {
                return (
                    <Panel
                        contentClassName="no-padding"
                        defaultActiveElement={".repos-fork-panel-repository-name"}
                        description={Resources.CustomizeQueriesPanelDescription}
                        footerButtonProps={[
                            {
                                id: "add-section",
                                onClick: onNewQuery,
                                primary: true,
                                text: Resources.AddSection
                            },
                            {
                                id: "reset-sections",
                                onClick: onResetQueries,
                                text: Resources.Reset,
                                tooltipProps: { text: Resources.ResetSectionsTooltip }
                            }
                        ]}
                        lightDismiss={true}
                        onDismiss={() => {
                            onDismiss();
                        }}
                        overlayContent={showOverlay ? <Spinner /> : undefined}
                        titleProps={{ text: Resources.CustomizeQueriesPanelTitle }}
                    >
                        <div className="rhythm-vertical-32 flex-grow">
                            <Observer errorMessage={errorMessage}>
                                {({ errorMessage }: { errorMessage: string }) => {
                                    return errorMessage && <MessageCard severity={MessageCardSeverity.Error}>{errorMessage}</MessageCard>;
                                }}
                            </Observer>
                            <div className="flex-row scroll-auto">
                                <ScrollableList
                                    ariaLabel={Resources.Sections}
                                    itemProvider={queries}
                                    renderRow={(index: number, query: IPullRequestQuery, details: IListItemDetails<IPullRequestQuery>) => {
                                        return (
                                            <ListItem key={query.id} index={index} details={details}>
                                                <div className="pr-query-row flex-grow flex-row flex-center body-xl scroll-hidden">
                                                    <div className="flex-grow flex-row margin-right-16 scroll-hidden pr-query-row-title rhythm-horizontal-8">
                                                        <Tooltip text={query.title} overflowOnly={true}>
                                                            <span className="text-ellipsis">{query.title}</span>
                                                        </Tooltip>
                                                        {!query.includeDuplicates && (
                                                            <Tooltip text={Resources.ExcludeDuplicates}>
                                                                <Pill excludeTabStop={true} size={PillSize.compact}>
                                                                    {Resources.NoDuplicates}
                                                                </Pill>
                                                            </Tooltip>
                                                        )}
                                                        {query.readonly && (
                                                            <Icon
                                                                ariaLabel={Resources.SectionIsReadonly}
                                                                className="flex-noshrink"
                                                                iconName="Lock"
                                                                size={IconSize.small}
                                                                tooltipProps={{ text: Resources.SectionIsReadonly }}
                                                            />
                                                        )}
                                                    </div>
                                                    <Button
                                                        ariaLabel={Resources.MoveSectionDown}
                                                        className="flex-noshrink bolt-list-cell-content-reveal margin-left-16"
                                                        disabled={index === queries.length - 1}
                                                        iconProps={{ iconName: "Down" }}
                                                        onClick={event => {
                                                            onMoveQuery(query, false);
                                                            event.preventDefault();
                                                        }}
                                                        subtle={true}
                                                        tooltipProps={{ text: Resources.MoveSectionDown }}
                                                    />
                                                    <Button
                                                        ariaLabel={Resources.MoveSectionUp}
                                                        className="flex-noshrink bolt-list-cell-content-reveal margin-left-16"
                                                        disabled={index === 0}
                                                        iconProps={{ iconName: "Up" }}
                                                        onClick={event => {
                                                            onMoveQuery(query, true);
                                                            event.preventDefault();
                                                        }}
                                                        subtle={true}
                                                        tooltipProps={{ text: Resources.MoveSectionUp }}
                                                    />
                                                    <Button
                                                        ariaLabel={Resources.RemoveSection}
                                                        className="flex-noshrink bolt-list-cell-content-reveal margin-left-16"
                                                        iconProps={{ iconName: "Delete" }}
                                                        onClick={event => {
                                                            onDeleteQuery(query);
                                                            event.preventDefault();
                                                        }}
                                                        subtle={true}
                                                        tooltipProps={{ text: Resources.RemoveSection }}
                                                    />
                                                    <Icon
                                                        className="flex-noshrink margin-left-16"
                                                        iconName="ChevronRightMed"
                                                        size={IconSize.medium}
                                                    />
                                                </div>
                                            </ListItem>
                                        );
                                    }}
                                    width="100%"
                                    singleClickActivation={true}
                                    onActivate={(event, queryRow) => {
                                        // Avoid a side effect when navigating to the Query Panel, if we don't preventDefault,
                                        // the new focused button in that panel would be clicked immediately 🤯
                                        event.preventDefault();

                                        onEditQuery(queryRow.data);
                                    }}
                                    outerClassName="custom-scrollbar"
                                />
                            </div>
                        </div>
                    </Panel>
                );
            }}
        </Observer>
    );
};
